import { useQuery } from "@tanstack/react-query";
import type { userSearchAPIInterface } from "@dealer-center/ddc-api-user-interface";
import { CONFIGURATION_QUERY_KEY_BASE } from "./configurationRequest";

/**
 * The subject's CC username, always passed in — never resolved here.
 *
 * Invariant 4 of contracts/fetch-hooks.md: a hook that read the outlet context itself would let the
 * subject the page shows and the subject the query fetches drift apart. Optional because the caller
 * may render before the context resolves; the query stays disabled until it arrives.
 */
export interface UseGetBridgeIdOptions {
  /** The **CC** username — CC9's `userId`, the value the list and profile reads are keyed on. */
  userId?: string;
}

/**
 * The route the ControlCenter lambda already calls for its own bridge lookup.
 *
 * Not `/landing/nxlambda/configuration/*`, so this hook does **not** go through
 * `configurationRequest` — that builder is hardwired to the Configuration BFF's base path and its
 * `resource`/`accountId` shape, neither of which fits here.
 */
const USER_PROFILE_PATH = "/landing/nxlambda/userSearch";

/**
 * The **selected** user's Bridge ID, for the card's identity header (CC6 parity, reviewer request
 * 2026-09-17).
 *
 * **Not for the page heading.** That subheading names the *signed-in operator* and reads
 * `ccUserInfo.bridge_user_name` straight off the outlet context — session-scoped, free, and correct
 * for that row. Pointing it here instead is the 2026-09-18 bug: this hook answers about whoever it
 * is keyed on, so keying it on the context user named the **as-user** while impersonating.
 *
 * ## ⚠️ The Bridge ID is `username`, NOT `platformId`
 *
 * CC9's `/user-profile/{ccUsername}` names the fields from the *bridge's* point of view, and reading
 * them the other way round is the trap here:
 *
 * | CC9 field | What it is | Header shows it? |
 * |---|---|---|
 * | `username` | the **Bridge** username, e.g. `BRIDGEapezone665` | **yes — this is the Bridge ID** |
 * | `userId` | the **CC** username, e.g. `apezone665` | already shown, from the subject |
 * | `platformId` | an opaque GUID (`59ccaea3-…`), often `null` | no |
 *
 * `libs/ddc/pages/user-search` reached the same conclusion first: its results table labels
 * `username` "Bridge ID" and leaves `platformId` commented out as "Platform ID". This hook follows
 * that, and `UserDetail.platformId` — the field a reader is most likely to reach for, because it is
 * already on the profile response — is **not** the value.
 *
 * **Verified against live CC9 nonprod, 2026-09-18**, probing `/user-profile` for the 41 users on
 * `dealerdemo` plus the reviewer's example. `apezone665` returns
 * `{ platformId: "a10d5b84-…", username: "BRIDGEapezone665", registered: true, ddcUserProfile: {
 * userId: "apezone665", contact: { firstName: "Ape", lastName: "Zone665", … } } }` — i.e. exactly
 * the `Ape Zone665 (apezone665) • Bridge ID: BRIDGEapezone665` the request asked for, which is what
 * settles `username` as the field. Two facts from that run worth keeping:
 *
 * 1. **Absent is the common case: 11 of 42 users had a profile at all.** `dealerdemo-admin` — the
 *    usual local impersonation target — is one of the 404s, so the header shows no Bridge ID there
 *    and that is correct, not a broken read. To see the line locally, impersonate someone who has
 *    one (e.g. `/cc/as/dealerdemo/brbddemo`).
 * 2. **The Bridge ID usually equals the CC username** (`brbddemo` → `"brbddemo"`); only
 *    `apezone665` carried the `BRIDGE`-prefixed form, so that prefix is not a rule and must not be
 *    synthesised. **Showing the field anyway when it duplicates the username is a reviewer decision
 *    (2026-09-18)**: its presence is itself the information — the user is bridge-registered —
 *    which a reader cannot infer from an omitted line.
 *
 * ## Keyed on the subject, which is why this exists at all
 *
 * The obvious cheaper source is `ccUserInfo.bridge_user_name` from ControlCenter's outlet context.
 * **It is the wrong person.** That lookup runs against `internalUserId || userId`
 * (`ddc-pages-controlcenter-lambda.ts`), and `internalUserId` is always set while "as"-ing — so in
 * an impersonation context it reports the **operator's** bridge identity, not the as-user's. The
 * same defect already forces `IdentityHeader` to take the display name from the profile record
 * rather than from `subject.displayName`; this is that lesson applied to one more field. Keyed on
 * `userId`, the answer always describes whoever the header names.
 *
 * This also closes research **R-003**, which concluded no per-user bridge source existed: it
 * searched for a bridge *status* flag and did not reach this endpoint, which the ControlCenter
 * lambda was already calling per user.
 *
 * ## A user with no bridge profile is normal, not a failure
 *
 * Unlike every other hook in this lib, a non-2xx resolves to `bridgeId: null` instead of throwing.
 * CC9 404s for a user who has never registered a Bridge ID, which is a fact about the user and not
 * a read failure — the header simply omits the segment (FR-019 names three required values and this
 * is not one of them). Throwing would put an error state on a header that has nothing to report,
 * and `retry` would then hammer a 404. That is why there is no `isError` path for a caller to
 * handle.
 *
 * Route caveat for review: `/userSearch/{name}` belongs to the **user-search** page's lambda, not
 * Configuration's. It is reused rather than duplicated because it already serves exactly this read
 * and is `isInternalUser`-guarded, which every Configuration route is too (the whole subtree sits
 * behind `StrictlyInternalGuard`). If Configuration should own the call, it becomes a sixth
 * `userprofile` resource on its own BFF with no change to this signature.
 */
export const useGetBridgeId = ({ userId }: UseGetBridgeIdOptions) =>
  useQuery({
    // `userId` is in the key so an "as"-context switch, or a different selected row, cannot be
    // served another user's cached Bridge ID (invariant 2).
    queryKey: [CONFIGURATION_QUERY_KEY_BASE, "bridgeid", userId],
    queryFn: async (): Promise<{ bridgeId: string | null }> => {
      // Unreachable while `enabled` holds; it narrows the optional identifier without a cast.
      if (!userId) {
        throw new Error("useGetBridgeId requires a userId");
      }

      const response = await fetch(
        `${USER_PROFILE_PATH}/${encodeURIComponent(userId)}`
      );

      if (!response.ok) {
        return { bridgeId: null };
      }

      // The one unvalidated cast, as in `configurationRequest` and for the same reason.
      const profile = (await response.json()) as userSearchAPIInterface;

      return { bridgeId: profile?.username?.trim() || null };
    },
    enabled: Boolean(userId),
    retry: 1,
    staleTime: 1000 * 60 * 5
  });
